import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import { notify } from '../notifications';
import { apiErrorMessage } from '../admin/reports/edit/editApi';
import { saveStaffPermissions } from './staffApi';
import { StaffMember, StaffTarget, StaffTreeModule } from './types';
import { normalizeSelection, sameCodes } from './staffUtils';
import { outlinedButtonSx, primaryButtonSx } from './staffStyles';
import StaffPermissionTree from './StaffPermissionTree';

interface StaffPermissionsPanelProps {
  target: StaffTarget;
  member: StaffMember;
  tree: StaffTreeModule[];
  /** Без права на изменение дерево только для просмотра */
  canEdit: boolean;
  onSaved: () => void;
}

const panelSx = {
  p: { xs: 2, sm: 3 },
  borderRadius: '16px',
  bgcolor: '#ffffff',
  border: '1px solid #e2e8f0',
  boxShadow: '0 2px 10px rgba(0,0,0,0.03)'
};

/** Права сотрудника: черновик галочек сохраняется одной кнопкой, сервер пересекает их с правами точки */
const StaffPermissionsPanel: React.FC<StaffPermissionsPanelProps> = ({ target, member, tree, canEdit, onSaved }) => {
  const saved = useMemo(() => normalizeSelection(member.permissions, tree), [member.permissions, tree]);
  const savedKey = saved.join(',');
  const [draft, setDraft] = useState<string[]>(saved);
  const [saving, setSaving] = useState(false);

  // Список точки перезагружается после любого действия в карточке (статус, сессии): черновик
  // сбрасывается, только если на сервере изменились сами права
  useEffect(() => { setDraft(savedKey ? savedKey.split(',') : []); }, [savedKey]);

  const current = useMemo(() => normalizeSelection(draft, tree), [draft, tree]);
  const dirty = !sameCodes(current, saved);

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await saveStaffPermissions(target, member.id, current);
      notify.success(result.message);
      onSaved();
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Не удалось сохранить права'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper elevation={0} sx={panelSx}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a' }}>Доступные разделы</Typography>
        <Typography variant="caption" sx={{ color: '#64748b' }}>
          {canEdit
            ? 'Сервер применяет изменения сразу, меню сотрудника обновится при следующем открытии портала'
            : 'Права сотрудника доступны только для просмотра'}
        </Typography>
      </Box>

      <StaffPermissionTree tree={tree} value={current} onChange={canEdit ? setDraft : undefined} disabled={saving} />

      {canEdit && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 2.5 }}>
          <Button variant="outlined" onClick={() => setDraft(saved)} disabled={!dirty || saving} sx={outlinedButtonSx}>
            Отменить
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={handleSave}
            disabled={!dirty || saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={primaryButtonSx}
          >
            Сохранить права
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default StaffPermissionsPanel;
